import * as React from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import TradeInLayout from '~/Layouts/TradeIn/Layouts/TradeInLayout/TradeInLayout';
import { RootState, useAppSelector } from '~/Store';
import { tradeinActions } from '~/Store/tradeinSlice';
import CategorySkeleton from '~/components/CategorySkeleton/CategorySkeleton';
import { FILTERS_TYPE } from '~/components/Filters/Filters';
import { sortItems } from '~/utils/sort/settings';
import { LISTS_FOR_DISPLAY_TYPE, getAvailableFiltersForLists, getPreparedLists } from '~/utils/tradein';
import { isEmptyObject } from '~/utils/utils';

const stateSelector = (state: RootState) => {
    return {
        selectedTargetItem: state.tradein.selectedTargetItem,
        selectedSourceItem: state.tradein.selectedSourceItem,
        choosenItemFor: state.tradein.choosenItemFor,
        currentPage: state.app.currentPage,
        lists: state.tradein.lists,
        activeFilters: state.tradein.activeFilters,
        balance: state.account.balance,
        categories: state.app.categories,
        sortedBy: state.app.sortedBy,
    };
};

interface ITradeInContainer {
    categoryName?: string;
}

const TradeInContainer = ({ categoryName }: ITradeInContainer) => {
    const dispatch = useDispatch();
    const { selectedTargetItem, selectedSourceItem, currentPage, lists, choosenItemFor, activeFilters, balance, categories, sortedBy } = useAppSelector(stateSelector, shallowEqual);
    const currentTradeInLists = lists[categoryName || currentPage?.name];
    const state = React.useMemo<LISTS_FOR_DISPLAY_TYPE>(() => {
        const response = getPreparedLists(currentTradeInLists, choosenItemFor, selectedTargetItem, selectedSourceItem, activeFilters);
        if (sortedBy?.[currentPage?.name]?.name) {
            response.availableItems = sortItems(sortedBy[currentPage?.name].name, sortedBy[currentPage?.name].method, response.availableItems);
        }
        return response;
    }, [choosenItemFor, currentTradeInLists, activeFilters, sortedBy?.[currentPage?.name]]);
    const availableFilters = React.useMemo<FILTERS_TYPE>(() => {
        if (!state.availableItemsWithoutFilters?.length) {
            return {};
        }
        return getAvailableFiltersForLists(state.availableItemsWithoutFilters);
    }, [choosenItemFor, currentTradeInLists]);

    React.useEffect(() => {
        return () => {
            if (!document.location.href.includes('/port/')) {
                dispatch(tradeinActions.clearState());
            }
        };
    }, [currentPage?.name]);

    if (!lists || isEmptyObject(lists)) {
        return <CategorySkeleton />;
    }

    return (
        <TradeInLayout
            type={currentTradeInLists?.[0]?.type}
            selectedTargetItem={selectedTargetItem}
            selectedSourceItem={selectedSourceItem}
            availableItems={state.availableItems}
            choosenItemFor={choosenItemFor}
            availableFilters={availableFilters}
            activeFilters={activeFilters}
            balance={balance}
            category={categories[currentPage?.name]}
        />
    );
};

export default TradeInContainer;
